<template>
    <div class="history" :class="{ 'st-open': isOpen }" v-if="versions.length > 1" :style="historyStyle">
        <p>查看历史版本</p>
        <div
            class="input--dropdown-select"
            :class="{ 'st-open': isOpen }"
            ref="root"
        >
            <!-- layout 이 닫힘 크기를 예약해서, transform 이 펼쳐져도 아래 콘텐츠가 밀리지 않는다. -->
            <div class="dropdown-select__layout" :style="layoutStyle">
                <div
                    class="dropdown-select__transform"
                    ref="transform"
                    :style="transformStyle"
                >
                    <div
                        class="dropdown-select__display"
                        ref="display"
                        role="button"
                        tabindex="0"
                        @click="toggle"
                        @keydown.enter.prevent="toggle"
                        @keydown.space.prevent="toggle"
                    >
                        {{ selected.label }}
                    </div>
                    <div class="dropdown-select__option">
                        <ul class="dropdown-select__option__list" ref="list">
                            <li
                                v-for="version in versions"
                                :key="version.value"
                                class="dropdown-select__option__item"
                                :class="{ 'st-selected': version.value === value }"
                                role="option"
                                :aria-selected="version.value === value"
                                tabindex="0"
                                @click="onSelect(version.value)"
                                @keydown.enter.prevent="onSelect(version.value)"
                                @keydown.space.prevent="onSelect(version.value)"
                            >
                                <span>{{ version.label }}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import { getVersions, getVersionPath } from '@/pages/help/docVersions'

    export default {
        name: 'DocHistory',
        props: {
            docKey: { type: String, required: true },
            value: { type: String, required: true },
        },
        data() {
            return {
                isOpen: false,
                closeHeight: 0,
                openHeight: 0,
                layoutWidth: 0,
                measureTimer: null,
                lastWindowWidth: 0,
            }
        },
        computed: {
            versions() {
                return getVersions(this.docKey)
            },
            selected() {
                return this.versions.find(v => v.value === this.value) || this.versions[0]
            },
            layoutStyle() {
                if (!this.closeHeight) return {}
                return {
                    width: `${this.layoutWidth}px`,
                    height: `${this.closeHeight}px`,
                }
            },
            transformStyle() {
                if (!this.closeHeight) return {}
                return { height: `${this.isOpen ? this.openHeight : this.closeHeight}px` }
            },
            // 페이지 최하단이라 펼친 목록이 잘리지 않도록, 펼친 만큼 아래 여백을 확보한다.
            // 아래가 빈 공간이라 애니메이션 없이 즉시 잡아야 곧바로 스크롤할 수 있다.
            historyStyle() {
                const reserve = this.isOpen ? this.openHeight - this.closeHeight : 0
                return { paddingBottom: `${reserve}px` }
            },
        },
        mounted() {
            this.lastWindowWidth = window.innerWidth
            this.measure()
            // 웹폰트(Noto Sans SC)가 swap 으로 늦게 적용되므로 로드 완료 후 한 번 더 잰다.
            if (document.fonts && document.fonts.ready) {
                document.fonts.ready.then(this.measure)
            }
            window.addEventListener('resize', this.onResize)
            document.addEventListener('click', this.onDocumentClick)
            document.addEventListener('keydown', this.onKeydown)
        },
        beforeDestroy() {
            if (this.measureTimer) clearTimeout(this.measureTimer)
            window.removeEventListener('resize', this.onResize)
            document.removeEventListener('click', this.onDocumentClick)
            document.removeEventListener('keydown', this.onKeydown)
        },
        methods: {
            // transform 이 absolute 라 닫힘 폭/높이를 layout 에 직접 잡아준다.
            measure() {
                this.$nextTick(() => {
                    const transform = this.$refs.transform
                    const display = this.$refs.display
                    const list = this.$refs.list
                    if (!transform || !display || !list) return
                    if (!display.offsetHeight) {
                        this.measureTimer = setTimeout(this.measure, 100)
                        return
                    }
                    this.closeHeight = display.offsetHeight
                    this.openHeight = display.offsetHeight + list.offsetHeight
                    this.layoutWidth = transform.offsetWidth
                })
            },
            // 모바일에서 스크롤로 주소창이 접히면 세로만 바뀐 resize 가 발생하므로 가로 폭 변화만 본다.
            onResize() {
                if (window.innerWidth === this.lastWindowWidth) return
                this.lastWindowWidth = window.innerWidth
                this.isOpen = false
                this.measure()
            },
            onDocumentClick(event) {
                if (!this.isOpen) return
                if (this.$refs.root && !this.$refs.root.contains(event.target)) {
                    this.isOpen = false
                }
            },
            onKeydown(event) {
                if (event.key === 'Escape') this.isOpen = false
            },
            toggle() {
                this.isOpen = !this.isOpen
                if (this.isOpen) this.scrollIntoViewIfNeeded()
            },
            // 여백 확보 후에도 화면 밖으로 나가면 그만큼 스크롤해서 목록 전체가 보이게 한다.
            scrollIntoViewIfNeeded() {
                this.$nextTick(() => window.requestAnimationFrame(() => {
                    const transform = this.$refs.transform
                    if (!transform) return
                    const bottom = transform.getBoundingClientRect().top + this.openHeight
                    const overflow = bottom + 16 - window.innerHeight
                    if (overflow <= 0) return
                    if (window.scrollBy) {
                        try {
                            window.scrollBy({ top: overflow, behavior: 'smooth' })
                        } catch (e) {
                            window.scrollBy(0, overflow)
                        }
                    }
                }))
            },
            onSelect(version) {
                this.isOpen = false
                const path = getVersionPath(this.docKey, version)
                if (path === this.$route.path) return
                this.$router.push(path)
                window.scrollTo(0, 0)
            },
        },
    }
</script>
<style scoped>
    .history {
        display: flex;
        align-items: center;
        margin-top: 40px;
        color: #342e50;
    }
    .history p {
        margin: 0 14px 0 0;
        font-size: 15px;
        font-weight: 400;
        line-height: 24px;
    }

    .input--dropdown-select {
        display: inline-block;
        position: relative;
        width: auto;
        height: auto;
        color: rgba(52, 45, 81, 1);
    }
    .input--dropdown-select.st-open {
        z-index: 1000;
    }

    .dropdown-select__layout {
        position: relative;
        cursor: pointer;
    }

    /* 테두리 박스 하나 안에 display + 옵션 리스트를 두고 height 만 트윈한다. */
    .dropdown-select__transform {
        position: absolute;
        top: 0;
        left: 0;
        width: max-content;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        box-sizing: border-box;
        background-color: #fff;
        border: 1px solid rgba(235, 234, 238, 1);
        border-radius: 12px;
        transition: height 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
    }
    .st-open .dropdown-select__transform {
        transition: height 700ms cubic-bezier(0.165, 0.84, 0.44, 1);
    }

    .dropdown-select__display {
        position: relative;
        z-index: 3;
        display: flex;
        align-items: center;
        box-sizing: border-box;
        min-width: 50px;
        padding: 16px;
        font-size: 16px;
        font-weight: 400;
        line-height: 1;
        white-space: nowrap;
        cursor: pointer;
        background-color: #fff;
        transition: background-color 200ms cubic-bezier(0.16, 1, 0.3, 1);
    }
    /* 구분선을 border 대신 겹쳐 그려서, 디스플레이 박스 안에서 글자가 정확히 가운데 오도록 한다. */
    .dropdown-select__display::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 1px;
        background-color: rgba(235, 234, 238, 1);
    }
    @media (hover: hover) {
        .dropdown-select__display:hover {
            background-color: rgba(235, 234, 238, 1);
        }
    }
    .dropdown-select__display::before {
        content: '';
        position: absolute;
        right: 16px;
        top: 0;
        display: inline-block;
        width: 11px;
        height: 100%;
        background-image: url('../../assets/help/icn_arrow_dropdown.svg');
        background-repeat: no-repeat;
        background-size: contain;
        background-position: center 50%;
        transition: transform 300ms cubic-bezier(0.33, 1, 0.68, 1);
        will-change: transform;
    }
    .st-open .dropdown-select__display::before {
        transform: rotate(180deg);
    }

    .dropdown-select__option {
        overflow-y: auto;
        overflow-x: hidden;
        background-color: #fff;
    }
    .dropdown-select__option::-webkit-scrollbar {
        width: 5px;
        border-right: 2px solid transparent;
    }
    .dropdown-select__option::-webkit-scrollbar-thumb {
        background: linear-gradient(90deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 70%, rgba(0, 0, 0, 0) 70%, rgba(0, 0, 0, 0) 100%);
    }
    .dropdown-select__option::-webkit-scrollbar-track {
        background-color: transparent;
    }

    .dropdown-select__option__list {
        display: flex;
        flex-direction: column;
        margin: 0;
        padding: 0;
        list-style: none;
    }

    .dropdown-select__option__item {
        position: relative;
        display: flex;
        align-items: center;
        box-sizing: border-box;
        min-width: 50px;
        padding: 16px;
        font-size: 16px;
        font-weight: 400;
        line-height: 1;
        white-space: nowrap;
        cursor: pointer;
        background: #fff;
        border-bottom: 1px solid rgba(235, 234, 238, 1);
        transition: background-color 200ms cubic-bezier(0.16, 1, 0.3, 1);
    }
    .dropdown-select__option__item:last-child {
        border-bottom: none;
    }
    .dropdown-select__option__item::before {
        content: '';
        flex: 0 0 16px;
        width: 16px;
        height: 12px;
        margin-right: 8px;
        opacity: 0;
        background-image: url('../../assets/help/icn_checked.svg');
        background-repeat: no-repeat;
        background-size: contain;
        background-position: center;
    }
    .dropdown-select__option__item span {
        display: inline-block;
        position: relative;
        vertical-align: top;
        transform: translateX(-24px);
        transition: transform 200ms cubic-bezier(0.33, 1, 0.68, 1);
    }
    .dropdown-select__option__item.st-selected::before {
        opacity: 1;
    }
    .dropdown-select__option__item.st-selected span {
        transform: translateX(0);
    }
    @media (hover: hover) {
        .dropdown-select__option__item:hover {
            background-color: rgba(240, 239, 242, 1);
        }
    }
</style>
